// The kitchens dataset, extracted verbatim from pages/OurKitchens.tsx so the
// facility landing pages (data/facilityPages.ts) can share it.

export interface Equipment {
  name: string;
  icon: string;
}

export interface Service {
  name: string;
  icon: string;
}

export interface Kitchen {
  id: string;
  name: string;
  subtitle?: string;
  address: string;
  city: string;
  /**
   * Display-only overrides for the /our-kitchens card heading (CEO, 2026-09-21).
   * The card leads with the address and puts the name underneath, so it needs a
   * heading that reads as a heading — street THEN city, because these eight span
   * four municipalities and "2570 Cyril Street" alone does not tell anyone how
   * far away it is. `name` and `address` are deliberately untouched: they feed the
   * application form, the homepage tiles, the footer, the facility pages and the
   * map popups, and renaming a kitchen there is a different decision.
   * Street types are abbreviated (St, Ave) in these headings and only these: at the
   * full spelling two of the eight wrapped to a second line at every font size that is
   * still readable, because the heading is now an address and addresses are longer than
   * the names they replaced. `address` keeps the full spelling for the form and the
   * map popups.
   * Fall back to `address` / `name` when unset.
   */
  cardHeading?: string;
  cardName?: string;
  coordinates: [number, number];
  size: string;
  features: string[];
  idealFor: string[];
  certifications: string[];
  status: 'operating' | 'coming-soon';
  capacity: string;
  specialties: string[];
  description: string;
  keyAdvantage?: string;
  kitchenEquipment: Equipment[];
  includedServices: Service[];
  images?: string[];
  partnerLogo?: string;
}

// Dynamic kitchen data - easily expandable
// Station counts are the platform's registered active stations
// (operator_assets.assets, asset_type='station'), read 2026-09-03 — CMSY-45.
// Change them here only; /our-kitchens and the landing pages both read this file.
export const kitchens: Kitchen[] = [
  // ORDER IS DISPLAY ORDER on /our-kitchens — there is no sort. Kitsilano
  // leads because it is the commercial priority and it was buried: measured
  // 2026-09-06 it sat 5th of 8, reached by 40% of desktop and 68% of mobile
  // visitors, yet still drew the joint-most apply clicks (6) of any card.
  // Most clicks from the fewest viewers is the case for putting it first.
  {
    id: 'vancouver-kitsilano',
    name: 'Commissary Connect Kitsilano',
    subtitle: 'Vancouver - Kitsilano',
    address: '1605 W 5th Avenue, Vancouver, BC',
    city: 'Vancouver',
    cardHeading: '1605 W 5th Ave, Vancouver',
    cardName: "Commissary Connect Kitsilano · Kitsilano",
    coordinates: [49.2667, -123.1464],
    size: '5000 sq ft',
    capacity: '17 stations',
    features: ['Hourly Pay-Per-Use Booking', 'Business Accelerator', 'Prime Kitsilano Location', 'Culinary Community'],
    idealFor: ['Bakery & Pastry', 'Catering & Events', 'Food Truck', 'Meal Prep & Delivery', 'Restaurant & Ghost Kitchen', 'Startup & Small Batch'],
    certifications: ['Vancouver Coastal Health', 'Food-Safe Certified'],
    specialties: ['Business Accelerator', 'Culinary Community Hub', 'Partnership Model'],
    status: 'operating',
    description: 'Prime Kitsilano location, booked by the hour. Commissary Connect Kitsilano gives you more than just kitchen space - you get a business address in the heart of Kits, surrounded by affluent customers who line up for quality food, with no lease and no long-term commitment.\n\nThis 5,000 sq ft facility pairs a fully equipped catering kitchen with Commissary Connect\'s proven systems. With 17 stations and premium equipment, you\'ll work alongside established brands while building your own. The collaborative environment means networking opportunities and potential partnerships happen organically.\n\nKitsilano real estate is out of reach for most food startups, but this kitchen puts you right where your customers live, work, and eat. From yoga studios seeking healthy meal prep to tech offices needing catering, your market surrounds you. Don\'t settle for an industrial park kitchen when you could be in Kitsilano - apply now for this premium location.',
    keyAdvantage: 'Hourly pay-per-use access in prime Kitsilano location',
    kitchenEquipment: [
      { name: 'Convection Ovens', icon: '/icons/kitchen/convection-oven.svg' },
      { name: 'Double Low Burners', icon: '/icons/kitchen/burner.svg' },
      { name: 'Flat Top Grill', icon: '/icons/kitchen/grill.svg' },
      { name: '6 Burner Ranges', icon: '/icons/kitchen/range.svg' },
      { name: 'Fryers', icon: '/icons/kitchen/fryer.svg' },
      { name: 'Steamer', icon: '/icons/kitchen/steamer.svg' },
      { name: 'Walk-in Freezer', icon: '/icons/kitchen/freezer.svg' },
      { name: 'Walk-in Cooler', icon: '/icons/kitchen/cooler.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' }
    ],
    includedServices: [
      { name: 'Cleaning Supplies', icon: '/icons/kitchen/cleaning.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' },
      { name: 'Oil Disposal', icon: '/icons/kitchen/oil-disposal.svg' },
      { name: 'Equipment Maintenance', icon: '/icons/kitchen/maintenance.svg' },
      { name: 'Security Cameras', icon: '/icons/kitchen/security.svg' },
      { name: 'Rag Service', icon: '/icons/kitchen/rag-service.svg' },
      { name: 'Waste Disposal', icon: '/icons/kitchen/waste.svg' },
      { name: 'WiFi', icon: '/icons/kitchen/wifi.svg' },
      { name: 'Greywater Dumping', icon: '/icons/kitchen/greywater.svg' },
      { name: 'Utilities', icon: '/icons/kitchen/utilities.svg' },
      { name: 'Pest Control', icon: '/icons/kitchen/pest-control.svg' },
      { name: '24 Hour Access', icon: '/icons/kitchen/24-hour.svg' },
      { name: 'Online Booking', icon: '/icons/kitchen/online-booking.svg' }
    ],
    images: ['/kitsilano.jpg']
  },
  {
    id: 'vancouver-laurel',
    name: 'HACCP-Ready - BC Food Hub',
    subtitle: 'Vancouver - South',
    address: '1st Floor 107 8811 Laurel St, Vancouver, BC',
    city: 'Vancouver',
    cardHeading: '8811 Laurel St, 1st Floor, Vancouver',
    cardName: "HACCP-Ready — BC Food Hub · Unit 107, South Vancouver",
    coordinates: [49.2061, -123.1151],
    size: '5000 sq ft',
    capacity: '13 dedicated stations',
    features: ['HACCP-Ready', 'Quality Control Lab', 'Packaging Equipment', 'Cold Chain Storage'],
    idealFor: ['Bakery & Pastry', 'Catering & Events', 'Meal Prep & Delivery', 'CPG & Retail Products', 'Restaurant & Ghost Kitchen'],
    certifications: ['HACCP-Ready', 'Vancouver Coastal Health', 'Food-Safe Certified', 'SQF Ready', 'Export Facility'],
    specialties: ['Canada\'s First Shared-Use HACCP-Ready Site', 'CPG Production'],
    status: 'operating',
    description: 'Unlock retail doors that others can\'t open. As Western Canada\'s only shared-use HACCP-ready facility, we give you instant credibility with Safeway, Loblaws, Costco, and other major retailers who require HACCP certification. Our facility is built to support your HACCP certification journey - no more missed opportunities.\n\nOur 5,000 sq ft facility features everything serious food producers need: quality control lab, industrial packaging equipment, blast chillers, and cold chain storage that maintains product integrity from production to delivery. With 13 dedicated stations and comprehensive equipment, you have the space and tools needed for professional production runs.\n\nStop letting certification requirements hold your business back. Our HACCP-ready design and export documentation support means you can pursue any sales channel - from local grocery chains to international markets. Join successful brands already scaling here and transform your food business from startup to retail powerhouse.',
    keyAdvantage: 'Gateway to major retailers like Safeway, Loblaws, and Costco',
    kitchenEquipment: [
      { name: 'Convection Ovens', icon: '/icons/kitchen/convection-oven.svg' },
      { name: 'Double Low Burners', icon: '/icons/kitchen/burner.svg' },
      { name: 'Flat Top Grill', icon: '/icons/kitchen/grill.svg' },
      { name: '6 Burner Ranges', icon: '/icons/kitchen/range.svg' },
      { name: 'Fryers', icon: '/icons/kitchen/fryer.svg' },
      { name: 'Steamer', icon: '/icons/kitchen/steamer.svg' },
      { name: 'Walk-in Freezer', icon: '/icons/kitchen/freezer.svg' },
      { name: 'Walk-in Cooler', icon: '/icons/kitchen/cooler.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' }
    ],
    includedServices: [
      { name: 'Cleaning Supplies', icon: '/icons/kitchen/cleaning.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' },
      { name: 'Oil Disposal', icon: '/icons/kitchen/oil-disposal.svg' },
      { name: 'Equipment Maintenance', icon: '/icons/kitchen/maintenance.svg' },
      { name: 'Security Cameras', icon: '/icons/kitchen/security.svg' },
      { name: 'Rag Service', icon: '/icons/kitchen/rag-service.svg' },
      { name: 'Waste Disposal', icon: '/icons/kitchen/waste.svg' },
      { name: 'WiFi', icon: '/icons/kitchen/wifi.svg' },
      { name: 'Greywater Dumping', icon: '/icons/kitchen/greywater.svg' },
      { name: 'Utilities', icon: '/icons/kitchen/utilities.svg' },
      { name: 'Pest Control', icon: '/icons/kitchen/pest-control.svg' },
      { name: '24 Hour Access', icon: '/icons/kitchen/24-hour.svg' },
      { name: 'Online Booking', icon: '/icons/kitchen/online-booking.svg' },
      { name: 'Lunch Room', icon: '/icons/kitchen/lunch-room.svg' },
      { name: 'Office Area', icon: '/icons/kitchen/office.svg' }
    ],
    images: ['/haccp.webp']
  },
  {
    id: 'vancouver-laurel-2nd',
    name: 'BC Food Hub - Entry Level Kitchen',
    subtitle: 'South Vancouver',
    address: '8811 Laurel Street - 2nd Floor, Vancouver, BC',
    city: 'Vancouver',
    cardHeading: '8811 Laurel St, 2nd Floor, Vancouver',
    cardName: "BC Food Hub — Entry Level Kitchen · South Vancouver",
    coordinates: [49.2085, -123.1120],
    size: '1000 sq ft',
    capacity: '5 stations',
    features: ['VCH Approved', 'Entry-Level Kitchen', 'Walk-in Cooler', 'Professional Equipment'],
    idealFor: ['Bakery & Pastry', 'Catering & Events', 'Meal Prep & Delivery', 'Restaurant & Ghost Kitchen', 'Startup & Small Batch'],
    certifications: ['Vancouver Coastal Health', 'Food-Safe Certified'],
    specialties: ['Entry-Level Operations', 'Culinary Entrepreneurship'],
    status: 'operating',
    description: 'Start your food business without breaking the bank. This 1,000 sq ft entry-level kitchen removes the biggest barrier to your culinary dreams - cost. With no massive upfront investment, you can test recipes, build your customer base, and grow at your own pace.\n\nDon\'t let the "entry-level" name fool you - this is a fully equipped commercial kitchen with everything you need to succeed. Four dedicated stations, commercial ranges, convection ovens, and a walk-in cooler mean you can handle everything from small batch production to catering large events. Located in the same building as our HACCP-ready facility, you\'ll be part of a thriving food community where collaboration and growth happen naturally.\n\nPerfect for caterers starting out, meal prep businesses, or anyone testing a new food concept. Many of our success stories started right here, growing from weekend warriors to full-scale operations. Your food business journey starts with one simple step - book a tour and see your future kitchen today.',
    keyAdvantage: 'Perfect launching pad for culinary entrepreneurs with VCH approval',
    kitchenEquipment: [
      { name: 'Convection Ovens', icon: '/icons/kitchen/convection-oven.svg' },
      { name: 'Double Low Burners', icon: '/icons/kitchen/burner.svg' },
      { name: '6 Burner Ranges', icon: '/icons/kitchen/range.svg' },
      { name: 'Fryers', icon: '/icons/kitchen/fryer.svg' },
      { name: 'Walk-in Cooler', icon: '/icons/kitchen/cooler.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' }
    ],
    includedServices: [
      { name: 'Cleaning Supplies', icon: '/icons/kitchen/cleaning.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' },
      { name: 'Oil Disposal', icon: '/icons/kitchen/oil-disposal.svg' },
      { name: 'Equipment Maintenance', icon: '/icons/kitchen/maintenance.svg' },
      { name: 'Security Cameras', icon: '/icons/kitchen/security.svg' },
      { name: 'Rag Service', icon: '/icons/kitchen/rag-service.svg' },
      { name: 'Waste Disposal', icon: '/icons/kitchen/waste.svg' },
      { name: 'WiFi', icon: '/icons/kitchen/wifi.svg' },
      { name: 'Greywater Dumping', icon: '/icons/kitchen/greywater.svg' },
      { name: 'Utilities', icon: '/icons/kitchen/utilities.svg' },
      { name: 'Pest Control', icon: '/icons/kitchen/pest-control.svg' },
      { name: '24 Hour Access', icon: '/icons/kitchen/24-hour.svg' },
      { name: 'Online Booking', icon: '/icons/kitchen/online-booking.svg' },
      { name: 'Lunch Room', icon: '/icons/kitchen/lunch-room.svg' },
      { name: 'Office Area', icon: '/icons/kitchen/office.svg' }
    ],
    images: ['/entrylevel-laurel.webp']
  },
  {
    id: 'vancouver-terminal',
    name: 'Terminal Vancouver',
    subtitle: 'Baking & Confectionery Hub',
    address: '417 Industrial Avenue, Vancouver, BC',
    city: 'Vancouver',
    cardHeading: '417 Industrial Ave, Vancouver',
    cardName: "Terminal · Baking & Confectionery Hub",
    coordinates: [49.2745, -123.0970],
    size: '2500 sq ft',
    capacity: '9 stations',
    features: ['Baking Specialized', 'Pop-up Retail Space', 'Confectionery Equipment', 'Member Events'],
    idealFor: ['Bakery & Pastry', 'Catering & Events', 'Food Truck', 'Meal Prep & Delivery', 'Restaurant & Ghost Kitchen', 'Startup & Small Batch'],
    certifications: ['Vancouver Coastal Health', 'Food-Safe Certified'],
    specialties: ['Baking & Confectionery', 'Pop-up Events', 'Lighter Users'],
    status: 'operating',
    description: 'Turn your baking passion into profit with Vancouver\'s premier baking facility. This 2,500 sq ft specialized kitchen features commercial deck ovens, proofers, mixers, and everything pastry chefs and bakers dream about. But here\'s what sets us apart - your own pop-up retail space to sell directly to customers.\n\nImagine testing your croissants on real customers in the morning, getting instant feedback, and building a loyal following before committing to a storefront. Our pop-up space has launched dozens of successful bakeries, from artisan bread makers to wedding cake designers. With 9 dedicated stations and specialized confectionery equipment, you\'ll have room to grow from farmer\'s market vendor to wholesale supplier.\n\nEstablished in 2015, this kitchen has become the birthplace of Vancouver\'s most exciting baked goods brands. Whether you\'re perfecting sourdough at 4am or decorating cakes for weekend events, you\'ll find your tribe here. Stop dreaming about your bakery and start building it - your customers are waiting.',
    keyAdvantage: 'Pop-up retail space for showcasing creations and hosting events',
    kitchenEquipment: [
      { name: 'Convection Ovens', icon: '/icons/kitchen/convection-oven.svg' },
      { name: 'Steamer', icon: '/icons/kitchen/steamer.svg' },
      { name: 'Walk-in Cooler', icon: '/icons/kitchen/cooler.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' }
    ],
    includedServices: [
      { name: 'Cleaning Supplies', icon: '/icons/kitchen/cleaning.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' },
      { name: 'Oil Disposal', icon: '/icons/kitchen/oil-disposal.svg' },
      { name: 'Equipment Maintenance', icon: '/icons/kitchen/maintenance.svg' },
      { name: 'Security Cameras', icon: '/icons/kitchen/security.svg' },
      { name: 'Rag Service', icon: '/icons/kitchen/rag-service.svg' },
      { name: 'Waste Disposal', icon: '/icons/kitchen/waste.svg' },
      { name: 'WiFi', icon: '/icons/kitchen/wifi.svg' },
      { name: 'Greywater Dumping', icon: '/icons/kitchen/greywater.svg' },
      { name: 'Utilities', icon: '/icons/kitchen/utilities.svg' },
      { name: 'Pest Control', icon: '/icons/kitchen/pest-control.svg' },
      { name: '24 Hour Access', icon: '/icons/kitchen/24-hour.svg' },
      { name: 'Online Booking', icon: '/icons/kitchen/online-booking.svg' }
    ],
    images: ['/417.webp']
  },
  {
    id: 'vancouver-401',
    name: 'Vancouver - Original Kitchen',
    subtitle: '401 Industrial Avenue',
    address: '401 Industrial Avenue, Vancouver, BC',
    city: 'Vancouver',
    cardHeading: '401 Industrial Ave, Vancouver',
    cardName: "Established 2012 · near downtown",
    coordinates: [49.2715, -123.1005],
    size: '4500 sq ft',
    capacity: '18 stations',
    features: ['Original Location', 'Grease Handling', 'Ample Cookline', 'Near Downtown'],
    idealFor: ['Bakery & Pastry', 'Catering & Events', 'Food Truck', 'Meal Prep & Delivery', 'Restaurant & Ghost Kitchen', 'Startup & Small Batch'],
    certifications: ['Vancouver Coastal Health', 'Food-Safe Certified'],
    specialties: ['Established 2012', 'Central Location', 'SkyTrain Access'],
    status: 'operating',
    description: 'Where Vancouver\'s food success stories begin. Since 2012, this original 4,500 sq ft facility has launched more food trucks, catering companies, and restaurant brands than any other kitchen in the city. Located minutes from downtown with SkyTrain access, this is where serious food entrepreneurs come to scale.\n\nOur 18 stations provide extensive space for diverse operations - from food truck prep at dawn to ghost kitchen operations running multiple brands simultaneously. Heavy-duty ventilation handles the greasiest operations, while our extensive cookline provides the equipment variety needed for any cuisine. This isn\'t just a kitchen - it\'s a proven launching pad with over a decade of success stories.\n\nJoin the alumni who started here and now run multi-location restaurants, successful food truck fleets, and thriving catering empires. The infrastructure is proven, the location is unbeatable, and the community is supportive. Secure your production space in Vancouver\'s most established commissary kitchen today.',
    keyAdvantage: 'Original location with proven success stories since 2012',
    kitchenEquipment: [
      { name: 'Convection Ovens', icon: '/icons/kitchen/convection-oven.svg' },
      { name: 'Double Low Burners', icon: '/icons/kitchen/burner.svg' },
      { name: 'Flat Top Grill', icon: '/icons/kitchen/grill.svg' },
      { name: '6 Burner Ranges', icon: '/icons/kitchen/range.svg' },
      { name: 'Fryers', icon: '/icons/kitchen/fryer.svg' },
      { name: 'Steamer', icon: '/icons/kitchen/steamer.svg' },
      { name: 'Walk-in Freezer', icon: '/icons/kitchen/freezer.svg' },
      { name: 'Walk-in Cooler', icon: '/icons/kitchen/cooler.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' }
    ],
    includedServices: [
      { name: 'Cleaning Supplies', icon: '/icons/kitchen/cleaning.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' },
      { name: 'Oil Disposal', icon: '/icons/kitchen/oil-disposal.svg' },
      { name: 'Equipment Maintenance', icon: '/icons/kitchen/maintenance.svg' },
      { name: 'Security Cameras', icon: '/icons/kitchen/security.svg' },
      { name: 'Rag Service', icon: '/icons/kitchen/rag-service.svg' },
      { name: 'Waste Disposal', icon: '/icons/kitchen/waste.svg' },
      { name: 'WiFi', icon: '/icons/kitchen/wifi.svg' },
      { name: 'Greywater Dumping', icon: '/icons/kitchen/greywater.svg' },
      { name: 'Utilities', icon: '/icons/kitchen/utilities.svg' },
      { name: 'Pest Control', icon: '/icons/kitchen/pest-control.svg' },
      { name: '24 Hour Access', icon: '/icons/kitchen/24-hour.svg' },
      { name: 'Online Booking', icon: '/icons/kitchen/online-booking.svg' }
    ],
    images: ['/401.webp']
  },
  {
    id: 'abbotsford-fraser-valley',
    name: 'Fraser Valley Food Hub',
    subtitle: 'Abbotsford',
    address: '2570 Cyril Street, Abbotsford, BC',
    city: 'Abbotsford',
    cardHeading: '2570 Cyril St, Abbotsford',
    cardName: "Fraser Valley Food Hub",
    coordinates: [49.0504, -122.3045],
    size: '3500 sq ft',
    capacity: '10 stations',
    features: ['BC Food Hub Network', 'Collaboration Space', 'Innovation Hub', 'Regional Focus'],
    idealFor: ['Bakery & Pastry', 'Catering & Events', 'Food Truck', 'Meal Prep & Delivery', 'Restaurant & Ghost Kitchen', 'Startup & Small Batch'],
    certifications: ['Fraser Health Authority', 'Food-Safe Certified'],
    specialties: ['Fraser Valley Region', 'Artisan Foods', 'BC Food Hub Network'],
    status: 'operating',
    description: 'Tap into the Fraser Valley\'s booming food scene. As part of the BC Food Hub Network, this 3,500 sq ft Abbotsford facility connects you to the agricultural heart of BC. Located where the ingredients grow, you\'ll slash transportation costs while accessing the freshest local produce directly from farmers.\n\nWith 10 dedicated stations and lower overhead than Vancouver, your dollars go further here. This isn\'t just about cheaper rent - it\'s about being part of a collaborative food community that shares suppliers, splits shipping costs, and refers business to each other. Fraser Valley Food Hub members regularly collaborate on large orders, making competitive bids possible for smaller producers.\n\nAbbotsford is experiencing explosive growth, with new residents seeking local food options. Be the brand that captures this underserved market. Whether you\'re producing for farmers markets, local retailers, or Vancouver distribution, this strategic location cuts costs while expanding your reach. Smart food businesses are moving to Abbotsford - secure your space before the best stations are taken.',
    keyAdvantage: 'Part of BC Food Hub Network serving Fraser Valley artisans',
    kitchenEquipment: [
      { name: 'Convection Ovens', icon: '/icons/kitchen/convection-oven.svg' },
      { name: 'Double Low Burners', icon: '/icons/kitchen/burner.svg' },
      { name: '6 Burner Ranges', icon: '/icons/kitchen/range.svg' },
      { name: 'Walk-in Cooler', icon: '/icons/kitchen/cooler.svg' },
      { name: 'Walk-in Freezer', icon: '/icons/kitchen/freezer.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' }
    ],
    includedServices: [
      { name: 'Cleaning Supplies', icon: '/icons/kitchen/cleaning.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' },
      { name: 'Oil Disposal', icon: '/icons/kitchen/oil-disposal.svg' },
      { name: 'Equipment Maintenance', icon: '/icons/kitchen/maintenance.svg' },
      { name: 'Security Cameras', icon: '/icons/kitchen/security.svg' },
      { name: 'Rag Service', icon: '/icons/kitchen/rag-service.svg' },
      { name: 'Waste Disposal', icon: '/icons/kitchen/waste.svg' },
      { name: 'WiFi', icon: '/icons/kitchen/wifi.svg' },
      { name: 'Greywater Dumping', icon: '/icons/kitchen/greywater.svg' },
      { name: 'Utilities', icon: '/icons/kitchen/utilities.svg' },
      { name: 'Pest Control', icon: '/icons/kitchen/pest-control.svg' },
      { name: '24 Hour Access', icon: '/icons/kitchen/24-hour.svg' },
      { name: 'Online Booking', icon: '/icons/kitchen/online-booking.svg' }
    ],
    images: ['/AdobeStock_256842765-min-scaled-1.webp'],
    partnerLogo: '/fv-logo.png'
  },
  {
    id: 'surrey-commissary',
    name: 'Surrey Commissary Kitchen',
    subtitle: 'Surrey',
    address: '104 -15300 68 Avenue, Surrey, BC',
    city: 'Surrey',
    cardHeading: '15300 68 Ave, Surrey',
    cardName: "Surrey Commissary Kitchen · Unit 104",
    coordinates: [49.1044, -122.8011],
    size: '3000 sq ft',
    capacity: '15 stations',
    features: ['Central Location', 'Culinary Creativity Hub', 'Collaboration Space', 'Innovation Center'],
    idealFor: ['Bakery & Pastry', 'Catering & Events', 'Food Truck', 'Meal Prep & Delivery', 'Restaurant & Ghost Kitchen', 'Startup & Small Batch'],
    certifications: ['Fraser Health Authority', 'Food-Safe Certified'],
    specialties: ['Central Surrey Location', 'Creative Community', 'Multi-Purpose Space'],
    status: 'operating',
    description: 'Surrey is BC\'s next food frontier - and this kitchen is your gateway. With over 600,000 residents and growing, Surrey\'s diverse population craves authentic cuisine from around the world. This 3,000 sq ft central facility puts you at the epicenter of this untapped market, with easy access to both Surrey and Langley customers.\n\nOur 15 stations serve the most diverse food community in Metro Vancouver. From South Asian meal prep to Caribbean catering, our members reflect Surrey\'s multicultural landscape. This diversity creates unique opportunities - shared spice orders, cross-cultural collaborations, and access to specialty suppliers other kitchens can\'t offer.\n\nSurrey offers what Vancouver can\'t: affordable production space, ample parking, and a massive local market hungry for new food options. While others fight for customers in saturated Vancouver, you\'ll build a loyal following in Canada\'s fastest-growing city. The Surrey food rush is happening now - claim your station before your competition does.',
    keyAdvantage: 'Heart of culinary creativity in central Surrey location',
    kitchenEquipment: [
      { name: 'Convection Ovens', icon: '/icons/kitchen/convection-oven.svg' },
      { name: 'Double Low Burners', icon: '/icons/kitchen/burner.svg' },
      { name: '6 Burner Ranges', icon: '/icons/kitchen/range.svg' },
      { name: 'Walk-in Cooler', icon: '/icons/kitchen/cooler.svg' },
      { name: 'Walk-in Freezer', icon: '/icons/kitchen/freezer.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' }
    ],
    includedServices: [
      { name: 'Cleaning Supplies', icon: '/icons/kitchen/cleaning.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' },
      { name: 'Oil Disposal', icon: '/icons/kitchen/oil-disposal.svg' },
      { name: 'Equipment Maintenance', icon: '/icons/kitchen/maintenance.svg' },
      { name: 'Security Cameras', icon: '/icons/kitchen/security.svg' },
      { name: 'Rag Service', icon: '/icons/kitchen/rag-service.svg' },
      { name: 'Waste Disposal', icon: '/icons/kitchen/waste.svg' },
      { name: 'WiFi', icon: '/icons/kitchen/wifi.svg' },
      { name: 'Greywater Dumping', icon: '/icons/kitchen/greywater.svg' },
      { name: 'Utilities', icon: '/icons/kitchen/utilities.svg' },
      { name: 'Pest Control', icon: '/icons/kitchen/pest-control.svg' },
      { name: '24 Hour Access', icon: '/icons/kitchen/24-hour.svg' },
      { name: 'Online Booking', icon: '/icons/kitchen/online-booking.svg' }
    ],
    images: ['/To-Die-For-loaves-3.webp'],
    partnerLogo: '/sck-banner_transparent.svg'
  },
  {
    id: 'langley-pureprep',
    name: 'PurePrep Commissary Kitchen',
    subtitle: 'Langley',
    address: '20381 62 Avenue, Langley, BC',
    city: 'Langley',
    cardHeading: '20381 62 Ave, Langley',
    cardName: "PurePrep Commissary Kitchen",
    coordinates: [49.115118, -122.657801],
    size: '10,000 sq ft',
    capacity: '21 stations',
    features: ['State-of-the-Art Facility', '100-foot Cookline', 'Dedicated 10x10 Stations', 'Premium Turnkey Solution'],
    idealFor: ['Bakery & Pastry', 'Catering & Events', 'Food Truck', 'Meal Prep & Delivery', 'Restaurant & Ghost Kitchen', 'Startup & Small Batch'],
    certifications: ['Fraser Health Authority', 'Food-Safe Certified'],
    specialties: ['Scalable Operations', 'Advanced Equipment Tracking', 'Premium Facility'],
    status: 'operating',
    description: 'Experience the future of commercial kitchens today. PurePrep\'s massive 10,000 sq ft Langley facility isn\'t just big - it\'s brilliantly designed for efficiency. With a 100-foot cookline and 21 dedicated stations, you\'ll never wait for equipment or work around others\' schedules. This is industrial-scale production meets boutique-level service.\n\nYour dedicated 10x10 station is truly yours - with exclusive prep tables, storage racks, and designated cooler space. No more labeling every container or worrying about missing inventory. The equipment reads like a chef\'s wish list: 6-burner ranges, convection ovens, deep fryers, charbroilers, flat tops - all commercial grade, all maintained for you. This is the kitchen you\'d build if money was no object.\n\nLangley is where smart food businesses scale. Lower costs than Vancouver, easier delivery access throughout the Lower Mainland, and a rapidly growing local market. Whether you\'re meal prepping for hundreds, running multiple ghost kitchens, or producing for retail, PurePrep\'s technology-enabled facility tracks your usage, streamlines operations, and lets you focus on growth. Don\'t just rent kitchen space - invest in your food business\'s future at PurePrep.',
    keyAdvantage: 'State-of-the-art 10,000 sq ft facility with 100-foot cookline now operating',
    kitchenEquipment: [
      { name: 'Convection Ovens', icon: '/icons/kitchen/convection-oven.svg' },
      { name: '6 Burner Ranges', icon: '/icons/kitchen/range.svg' },
      { name: 'Fryers', icon: '/icons/kitchen/fryer.svg' },
      { name: 'Flat Top Grill', icon: '/icons/kitchen/grill.svg' },
      { name: 'Walk-in Cooler', icon: '/icons/kitchen/cooler.svg' },
      { name: 'Walk-in Freezer', icon: '/icons/kitchen/freezer.svg' },
      { name: 'Dishwasher', icon: '/icons/kitchen/dishwasher.svg' }
    ],
    includedServices: [
      { name: '24 Hour Access', icon: '/icons/kitchen/24-hour.svg' },
      { name: 'Cleaning Supplies', icon: '/icons/kitchen/cleaning.svg' },
      { name: 'Equipment Maintenance', icon: '/icons/kitchen/maintenance.svg' },
      { name: 'Security Cameras', icon: '/icons/kitchen/security.svg' },
      { name: 'WiFi', icon: '/icons/kitchen/wifi.svg' },
      { name: 'Office Area', icon: '/icons/kitchen/office.svg' },
      { name: 'Online Booking', icon: '/icons/kitchen/online-booking.svg' },
      { name: 'Utilities', icon: '/icons/kitchen/utilities.svg' },
      { name: 'Waste Disposal', icon: '/icons/kitchen/waste.svg' }
    ],
    images: ['/pureprep-langley.webp', '/pureprep-langley2.webp', '/pureprep-langley3.webp'],
    partnerLogo: '/pp-square-logo.png'
  }
];

// ── v2's facilities: the ONE definition of the kitchen → facility-id mapping ──
//
// This mapping used to live in three hand-maintained copies (facilityPages.ts,
// the form's KITCHENS list, and an if/else chain in OurKitchens) — CMSY-28,
// review finding 10. A wrong id books an application into the wrong kitchen, so
// it is defined once, here, next to the kitchens themselves.

export interface V2Facility {
  /** v2's facility id — the intake API's contract, not ours. */
  id: number;
  /** The name v2's own form shows, so the replica matches it exactly. */
  name: string;
  /** The kitchen in the dataset above, where one exists. */
  kitchenId: string;
}

// Ids, names and order read from GET /api/facilities/for-application-form on
// v2 production (2026-09-01) — that endpoint is the source of truth, and the
// order is the order v2's form renders them in. Id 3 still carries retired
// partner branding in v2's database; the site always says "Commissary Connect
// Kitsilano". Richmond (v2 id 9) was removed from this list on 2026-09-16
// (CMSY-96): it was pre-launch with nothing behind it. v2 still knows the id;
// we simply stop offering it. Do not re-add it without a facility to send
// people to.
export const V2_FACILITIES: V2Facility[] = [
  { id: 1, name: '401 Industrial', kitchenId: 'vancouver-401' },
  { id: 2, name: '417 Industrial', kitchenId: 'vancouver-terminal' },
  { id: 5, name: 'Laurel Street - Downstairs (HACCP)', kitchenId: 'vancouver-laurel' },
  { id: 4, name: 'Laurel Street Upstairs', kitchenId: 'vancouver-laurel-2nd' },
  { id: 3, name: 'Commissary Connect Kitsilano', kitchenId: 'vancouver-kitsilano' },
];

/**
 * The v2 facility id for a kitchen, or `undefined` for one v2 does not take
 * applications for. Never substitute a default: the old if/else fell through to
 * id 5, so an unknown kitchen silently preselected Laurel HACCP. Callers that
 * get `undefined` preselect nothing and let the form ask.
 */
export function v2FacilityIdFor(kitchenId: string): number | undefined {
  return V2_FACILITIES.find((f) => f.kitchenId === kitchenId)?.id;
}

/** A kitchen that takes applications on its own site rather than through v2.
 *  Who these operators are and their public websites: `partners.ts`. */
export interface OffsiteForm {
  kitchenId: string;
  /** As v2's own form lists it. */
  name: string;
  /**
   * v2's href, character for character — the replica must match v2, and
   * V2FormReplica.test.tsx pins it. (PurePrep's 301s to the trailing-slash form;
   * left as v2 has it. Verified 2026-09-04.) Absent = not on v2's form.
   */
  v2FormHref?: string;
  /** Where /our-kitchens hands off, carrying the sibling site's from-cc marker. */
  siteHandoffHref: string;
}

export const V2_OFFSITE_FORMS: OffsiteForm[] = [
  {
    kitchenId: 'surrey-commissary',
    name: 'Surrey Commissary Kitchen',
    v2FormHref: 'https://sck.commissaryconnect.com/forms/application-form',
    siteHandoffHref: 'https://sck.commissaryconnect.com/forms/application-form?from-cc=true',
  },
  {
    kitchenId: 'langley-pureprep',
    name: 'PurePrep Commissary (Langley)',
    v2FormHref: 'https://pureprep.commissaryconnect.com/forms/application-form',
    siteHandoffHref: 'https://pureprep.commissaryconnect.com/forms/application-form/?from-cc=true',
  },
  {
    kitchenId: 'abbotsford-fraser-valley',
    name: 'Fraser Valley Food Hub',
    siteHandoffHref: 'https://fv.commissaryconnect.com/forms/application-form/?from-cc=true',
  },
];

export function offsiteFormFor(kitchenId: string): OffsiteForm | undefined {
  return V2_OFFSITE_FORMS.find((f) => f.kitchenId === kitchenId);
}
